
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { User } from '@/types/user';
import { generateUniqueNickname } from '@/utils/nicknameGenerator';
import OAuthButtons from './OAuthButtons';
import EmailField from './fields/EmailField';
import PasswordField from './fields/PasswordField';
import LoginDivider from './LoginDivider';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

// Define validation schema
const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onLogin: (userData: User) => void;
  onSwitchToSignUp: () => void;
  onSwitchToRequestInvite: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ 
  onLogin, 
  onSwitchToSignUp,
  onSwitchToRequestInvite
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Initialize react-hook-form
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate a unique nickname
      const nickname = generateUniqueNickname();
      
      // Mock login (in a real app, this would be a call to an auth API)
      onLogin({
        id: 'user_' + Math.random().toString(36).substring(2, 15),
        name: 'Guest User',
        email: values.email,
        nickname,
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
        isAdmin: values.email.includes('admin'), // Simple mock for admin detection
        hasCompletedOnboarding: false,
        dateJoined: new Date(),
        lastLogin: new Date()
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Authentication error:', error);
      toast.error('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="text-center">
        <h1 className="text-3xl font-display font-semibold text-hormony-charcoal">
          Welcome Back
        </h1>
        <p className="mt-2 text-hormony-charcoal/70">
          Sign in to continue your hormone harmony journey
        </p>
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-8 space-y-6">
          <EmailField form={form} />
          <PasswordField form={form} />
          
          <div className="text-right">
            <a href="#" className="story-link text-sm">
              Forgot your password?
            </a>
          </div>
          
          <div>
            <Button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isLoading && <LoadingSpinner className="mr-2" />}
              Sign In
            </Button>
          </div>
          
          <div className="text-center mt-4">
            <button
              type="button"
              className="text-sm text-hormony-charcoal/70 hover:text-hormony-coral transition-colors"
              onClick={onSwitchToRequestInvite}
            >
              Don't have an account? Request an invite
            </button>
          </div>
        </form>
      </Form>
      
      <LoginDivider />
      <OAuthButtons isLoading={isLoading} setIsLoading={setIsLoading} />
    </>
  );
};

export default LoginForm;
